import React, { useState } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { Box, Button, Stack, Typography } from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import { OutlierConfigGrid } from '../grids';
import { AddEditOutlierConfigDialog } from '../dialogs';
import { useGetCenterQuery, useUpdateCenterMutation } from '../../../../services/ConfigurationService';
import { OutlierConfigType } from '../../../../services/ConfigurationService/types/centerType';

export const OutlierConfigTab: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const company = queryParams.get("company") || "";

  const { data: center, refetch } = useGetCenterQuery({ id: id || "", company }, { skip: !id || !company });
  const [updateCenter] = useUpdateCenterMutation();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingConfig, setEditingConfig] = useState<OutlierConfigType | null>(null);

  const outlierConfigs = center?.outlierConfigs || [];

  const handleAdd = () => {
    setEditingConfig(null);
    setDialogOpen(true);
  };

  const handleEdit = (config: OutlierConfigType) => {
    setEditingConfig(config);
    setDialogOpen(true);
  };

  const handleSave = async (config: OutlierConfigType) => {
    if (!center) return;

    let updatedConfigs: OutlierConfigType[];

    if (editingConfig) {
      updatedConfigs = outlierConfigs.map(existing =>
        existing.id === editingConfig.id ? config : existing
      );
    } else {
      updatedConfigs = [...outlierConfigs, config];
    }

    updatedConfigs.sort((a, b) =>
      new Date(b.effectiveDate).getTime() - new Date(a.effectiveDate).getTime()
    );

    const updatedCenter = {
      ...center,
      outlierConfigs: updatedConfigs
    };

    await updateCenter(updatedCenter);
    refetch();
  };

  const handleDelete = async (configToDelete: OutlierConfigType) => {
    if (!center) return;

    const updatedConfigs = outlierConfigs.filter(config => config.id !== configToDelete.id);

    const updatedCenter = {
      ...center,
      outlierConfigs: updatedConfigs
    };

    await updateCenter(updatedCenter);
    refetch();
  };

  const handleClose = () => {
    setDialogOpen(false);
    setEditingConfig(null);
  };

  return (
    <Stack spacing={3}>
      <Typography variant="h6" sx={{ fontWeight: 600 }}>
        Outlier Configurations
      </Typography>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={handleAdd}
        >
          Add Outlier Configuration
        </Button>
      </Box>

      <OutlierConfigGrid
        outlierConfigs={outlierConfigs}
        onRowSelect={handleEdit}
      />

      <AddEditOutlierConfigDialog
        open={dialogOpen}
        onClose={handleClose}
        onSave={handleSave}
        onDelete={handleDelete}
        editingConfig={editingConfig}
      />
    </Stack>
  );
};
